import { bigNumberify } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { multicall } from 'wagmi/actions'

import { PotSavingsConverter } from './PotSavingsConverter'
import { SavingsConverterQueryOptions, SavingsConverterQueryParams } from './types'
import { getChainConfigEntry } from '@/config/chain'
import { SavingsConverterQuery } from '@/config/chain/types'
import { sparkVaultV2Abi } from '@/config/contracts-generated'
import { TokenSymbol } from '@/domain/types/TokenSymbol'

export const sparkVaultV2SpusdcConverterQuery = sparkVaultV2SavingsConverterQueryFactory(TokenSymbol('spUSDC'))
export const sparkVaultV2SpusdtConverterQuery = sparkVaultV2SavingsConverterQueryFactory(TokenSymbol('spUSDT'))
export const sparkVaultV2SpethConverterQuery = sparkVaultV2SavingsConverterQueryFactory(TokenSymbol('spETH'))
export const sparkVaultV2SppyusdConverterQuery = sparkVaultV2SavingsConverterQueryFactory(TokenSymbol('spPYUSD'))

function sparkVaultV2SavingsConverterQueryFactory(savingsTokenSymbol: TokenSymbol): SavingsConverterQuery {
  return function sparkVaultV2SavingsConverterQuery({
    wagmiConfig,
    timestamp,
    chainId,
  }: SavingsConverterQueryParams): SavingsConverterQueryOptions {
    return queryOptions({
      queryKey: ['savings-info', savingsTokenSymbol, { chainId }] as QueryKey,
      queryFn: async () => {
        const chainConfig = getChainConfigEntry(chainId)
        const savingsTokenAddress = chainConfig.tokenDefinitionRepository.findOneBySymbol(savingsTokenSymbol).address

        const [vsr, rho, chi] = await multicall(wagmiConfig, {
          allowFailure: false,
          chainId,
          contracts: [
            {
              address: savingsTokenAddress,
              functionName: 'vsr',
              args: [],
              abi: sparkVaultV2Abi,
            },
            {
              address: savingsTokenAddress,
              functionName: 'rho',
              args: [],
              abi: sparkVaultV2Abi,
            },
            {
              address: savingsTokenAddress,
              functionName: 'chi',
              args: [],
              abi: sparkVaultV2Abi,
            },
          ],
        })

        return {
          vsr,
          rho,
          chi,
        }
      },
      select: ({ vsr, rho, chi }) => {
        return new PotSavingsConverter({
          potParams: {
            sr: bigNumberify(vsr),
            rho: bigNumberify(rho),
            chi: bigNumberify(chi),
          },
          currentTimestamp: timestamp,
        })
      },
    })
  }
}
